/*
 * Ravniks — LED fretboard engine.
 * Drives the photoreal R-01 renders with the same logic as the guitar's app:
 * chords (finger colours), scales (root / 3rd / 5th), CAGED shapes, progressions
 * and light-show effects. Also a tiny plucked-string synth for the demo.
 *
 * <rv-guitar data-orient="h|v|auto" data-color="black" data-mode="flow|power|off|...">
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- map */
  // [fret 0..22][string 6..1] -> [u, v] in the upright render (0..1).
  const MAP = [[[0.4482,0.1733],[0.4693,0.1732],[0.493,0.1732],[0.5145,0.1732],[0.5344,0.1732],[0.5532,0.1732]],[[0.447,0.1955],[0.469,0.1955],[0.4926,0.1955],[0.5145,0.1955],[0.5347,0.1955],[0.5539,0.1954]],[[0.4458,0.2199],[0.4688,0.2198],[0.4926,0.2198],[0.5144,0.2199],[0.5358,0.22],[0.5549,0.2199]],[[0.4444,0.2526],[0.4663,0.2526],[0.4917,0.2526],[0.5147,0.2526],[0.5364,0.2526],[0.5563,0.2526]],[[0.4431,0.2835],[0.4654,0.2835],[0.4913,0.2835],[0.5146,0.2835],[0.5374,0.2835],[0.5576,0.2835]],[[0.4418,0.3127],[0.4646,0.3127],[0.4909,0.3127],[0.5146,0.3127],[0.5382,0.3127],[0.5589,0.3127]],[[0.4402,0.3402],[0.4637,0.3402],[0.4906,0.3402],[0.5146,0.3402],[0.5391,0.3402],[0.5603,0.3402]],[[0.4392,0.3663],[0.4628,0.3663],[0.4902,0.3663],[0.5148,0.3663],[0.5398,0.3663],[0.5612,0.3663]],[[0.4381,0.3908],[0.4621,0.3908],[0.4898,0.3908],[0.5149,0.3908],[0.5405,0.3908],[0.5625,0.3908]],[[0.437,0.414],[0.4613,0.414],[0.4895,0.414],[0.515,0.414],[0.5413,0.414],[0.5635,0.414]],[[0.4359,0.4358],[0.4607,0.4358],[0.4892,0.4359],[0.5149,0.4359],[0.542,0.4359],[0.5645,0.4358]],[[0.4351,0.4564],[0.4601,0.4564],[0.4889,0.4564],[0.5149,0.4564],[0.5425,0.4564],[0.5652,0.4564]],[[0.4345,0.476],[0.4594,0.476],[0.4885,0.4759],[0.5152,0.476],[0.5432,0.476],[0.566,0.476]],[[0.4335,0.4944],[0.459,0.4944],[0.4883,0.4944],[0.5153,0.4944],[0.5437,0.4944],[0.5669,0.4944]],[[0.4328,0.5116],[0.4583,0.5116],[0.4879,0.5116],[0.5153,0.5116],[0.5444,0.5117],[0.5676,0.5116]],[[0.4321,0.528],[0.4578,0.528],[0.4878,0.528],[0.5154,0.528],[0.5448,0.528],[0.5682,0.528]],[[0.4314,0.5435],[0.4572,0.5435],[0.4875,0.5435],[0.5155,0.5435],[0.5455,0.5435],[0.5687,0.5435]],[[0.4307,0.5581],[0.4567,0.5581],[0.4872,0.5581],[0.5156,0.5581],[0.5459,0.5581],[0.5694,0.5581]],[[0.4302,0.5719],[0.4562,0.5718],[0.487,0.5718],[0.5155,0.5718],[0.5463,0.5718],[0.5699,0.5719]],[[0.4297,0.5849],[0.4559,0.5849],[0.4867,0.5849],[0.5157,0.5849],[0.5467,0.5849],[0.5704,0.5849]],[[0.4292,0.5971],[0.4553,0.5971],[0.4866,0.5971],[0.5156,0.5971],[0.5471,0.5971],[0.5709,0.5971]],[[0.4288,0.609],[0.4551,0.6091],[0.4867,0.6091],[0.5157,0.609],[0.5473,0.609],[0.5715,0.6091]],[[0.4299,0.6196],[0.4587,0.6196],[0.4866,0.6196],[0.5147,0.6196],[0.5425,0.6197],[0.5692,0.6196]]];
  const FRETS = MAP.length;          // 23 rows: 0 (open) .. 22
  const STRINGS = 6;                 // index 0 = low E (6th) .. 5 = high e (1st)
  const N = FRETS * STRINGS;

  /* ------------------------------------------------------------- colours */
  const C = {
    f1: '#ff3b55', f2: '#2ff28c', f3: '#3d7dff', f4: '#ffd23f', open: '#e9f6ff',
    root: '#ff3b55', third: '#2ff28c', fifth: '#ffd23f', tone: '#dfe9f5', cyan: '#5afcff', white: '#f4f8ff'
  };
  const FINGER = [C.open, C.f1, C.f2, C.f3, C.f4];

  /* -------------------------------------------------------------- theory */
  const TUNING = [40, 45, 50, 55, 59, 64];
  const NAMES = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B'];
  const NAME_ALT = { 1: 'D♭', 3: 'D♯', 6: 'G♭', 8: 'G♯', 10: 'A♯' };

  const CHORDS = {
    maj: { label: 'Major', sym: '', iv: [0, 4, 7] },
    min: { label: 'Minor', sym: 'm', iv: [0, 3, 7] },
    dom7: { label: '7', sym: '7', iv: [0, 4, 7, 10] },
    maj7: { label: 'Maj7', sym: 'maj7', iv: [0, 4, 7, 11] },
    min7: { label: 'm7', sym: 'm7', iv: [0, 3, 7, 10] },
    sus2: { label: 'sus2', sym: 'sus2', iv: [0, 2, 7] },
    sus4: { label: 'sus4', sym: 'sus4', iv: [0, 5, 7] },
    add9: { label: 'add9', sym: 'add9', iv: [0, 4, 7, 2] },
    six: { label: '6', sym: '6', iv: [0, 4, 7, 9] },
    dim: { label: 'dim', sym: 'dim', iv: [0, 3, 6] },
    aug: { label: 'aug', sym: 'aug', iv: [0, 4, 8] }
  };
  const CHORD_LONG = { maj: 'major', min: 'minor', dom7: 'dominant seventh', maj7: 'major seventh', min7: 'minor seventh', sus2: 'suspended second', sus4: 'suspended fourth', add9: 'add nine', six: 'sixth', dim: 'diminished', aug: 'augmented' };

  // Open voicings: frets [6..1] (null = muted, 0 = open) + fingers.
  const OPEN = {
    'C:maj': [[null, 3, 2, 0, 1, 0], [0, 3, 2, 0, 1, 0]],
    'A:maj': [[null, 0, 2, 2, 2, 0], [0, 0, 1, 2, 3, 0]],
    'G:maj': [[3, 2, 0, 0, 0, 3], [2, 1, 0, 0, 0, 3]],
    'E:maj': [[0, 2, 2, 1, 0, 0], [0, 2, 3, 1, 0, 0]],
    'D:maj': [[null, null, 0, 2, 3, 2], [0, 0, 0, 1, 3, 2]],
    'A:min': [[null, 0, 2, 2, 1, 0], [0, 0, 2, 3, 1, 0]],
    'E:min': [[0, 2, 2, 0, 0, 0], [0, 2, 3, 0, 0, 0]],
    'D:min': [[null, null, 0, 2, 3, 1], [0, 0, 0, 2, 3, 1]],
    'C:dom7': [[null, 3, 2, 3, 1, 0], [0, 3, 2, 4, 1, 0]],
    'A:dom7': [[null, 0, 2, 0, 2, 0], [0, 0, 2, 0, 3, 0]],
    'G:dom7': [[3, 2, 0, 0, 0, 1], [3, 2, 0, 0, 0, 1]],
    'E:dom7': [[0, 2, 0, 1, 0, 0], [0, 2, 0, 1, 0, 0]],
    'D:dom7': [[null, null, 0, 2, 1, 2], [0, 0, 0, 2, 1, 3]],
    'B:dom7': [[null, 2, 1, 2, 0, 2], [0, 2, 1, 3, 0, 4]],
    'C:maj7': [[null, 3, 2, 0, 0, 0], [0, 3, 2, 0, 0, 0]],
    'F:maj7': [[null, null, 3, 2, 1, 0], [0, 0, 3, 2, 1, 0]],
    'A:maj7': [[null, 0, 2, 1, 2, 0], [0, 0, 2, 1, 3, 0]],
    'D:maj7': [[null, null, 0, 2, 2, 2], [0, 0, 0, 1, 1, 1]],
    'E:maj7': [[0, 2, 1, 1, 0, 0], [0, 3, 1, 2, 0, 0]],
    'A:min7': [[null, 0, 2, 0, 1, 0], [0, 0, 2, 0, 1, 0]],
    'E:min7': [[0, 2, 2, 0, 3, 0], [0, 1, 2, 0, 4, 0]],
    'D:min7': [[null, null, 0, 2, 1, 1], [0, 0, 0, 2, 1, 1]],
    'A:sus2': [[null, 0, 2, 2, 0, 0], [0, 0, 1, 2, 0, 0]],
    'D:sus2': [[null, null, 0, 2, 3, 0], [0, 0, 0, 1, 3, 0]],
    'A:sus4': [[null, 0, 2, 2, 3, 0], [0, 0, 1, 2, 3, 0]],
    'D:sus4': [[null, null, 0, 2, 3, 3], [0, 0, 0, 1, 3, 4]],
    'E:sus4': [[0, 2, 2, 2, 0, 0], [0, 2, 3, 4, 0, 0]],
    'C:add9': [[null, 3, 2, 0, 3, 0], [0, 2, 1, 0, 3, 0]]
  };
  // Movable shapes: offsets from the root fret r ([6..1]) + fingers.
  const SHAPE_E = {
    maj: [[0, 2, 2, 1, 0, 0], [1, 3, 4, 2, 1, 1]],
    min: [[0, 2, 2, 0, 0, 0], [1, 3, 4, 1, 1, 1]],
    dom7: [[0, 2, 0, 1, 0, 0], [1, 3, 1, 2, 1, 1]],
    maj7: [[0, null, 1, 1, 0, null], [1, 0, 3, 4, 2, 0]],
    min7: [[0, 2, 0, 0, 0, 0], [1, 3, 1, 1, 1, 1]],
    sus4: [[0, 2, 2, 2, 0, 0], [1, 2, 3, 4, 1, 1]],
    dim: [[0, 1, 2, 0, null, null], [1, 2, 3, 1, 0, 0]],
    aug: [[0, 3, 2, 1, 1, 0], [1, 4, 3, 2, 2, 1]]
  };
  const SHAPE_A = {
    maj: [[null, 0, 2, 2, 2, 0], [0, 1, 2, 3, 4, 1]],
    min: [[null, 0, 2, 2, 1, 0], [0, 1, 3, 4, 2, 1]],
    dom7: [[null, 0, 2, 0, 2, 0], [0, 1, 3, 1, 4, 1]],
    maj7: [[null, 0, 2, 1, 2, 0], [0, 1, 3, 2, 4, 1]],
    min7: [[null, 0, 2, 0, 1, 0], [0, 1, 3, 1, 2, 1]],
    sus2: [[null, 0, 2, 2, 0, 0], [0, 1, 3, 4, 1, 1]],
    sus4: [[null, 0, 2, 2, 3, 0], [0, 1, 2, 3, 4, 1]],
    add9: [[null, 0, 2, 4, 2, 0], [0, 1, 2, 4, 3, 1]],
    six: [[null, 0, 2, 2, 2, 2], [0, 1, 3, 3, 3, 3]],
    dim: [[null, 0, 1, 2, 1, null], [0, 1, 2, 4, 3, 0]],
    aug: [[null, 0, 3, 2, 2, 1], [0, 1, 4, 3, 3, 2]]
  };

  const SCALES = {
    major: { label: 'Major', iv: [0, 2, 4, 5, 7, 9, 11] },
    minor: { label: 'Natural minor', iv: [0, 2, 3, 5, 7, 8, 10] },
    pentMaj: { label: 'Major pentatonic', iv: [0, 2, 4, 7, 9] },
    pentMin: { label: 'Minor pentatonic', iv: [0, 3, 5, 7, 10] },
    blues: { label: 'Blues', iv: [0, 3, 5, 6, 7, 10] },
    dorian: { label: 'Dorian', iv: [0, 2, 3, 5, 7, 9, 10] },
    mixolydian: { label: 'Mixolydian', iv: [0, 2, 4, 5, 7, 9, 10] },
    harmMin: { label: 'Harmonic minor', iv: [0, 2, 3, 5, 7, 8, 11] }
  };

  const PROGRESSIONS = {
    'I-IV-V': { label: 'I – IV – V', steps: [[0, 'maj'], [5, 'maj'], [7, 'maj'], [0, 'maj']] },
    pop: { label: 'I – V – vi – IV', steps: [[0, 'maj'], [7, 'maj'], [9, 'min'], [5, 'maj']] },
    jazz: { label: 'ii – V – I', steps: [[2, 'min7'], [7, 'dom7'], [0, 'maj7'], [0, 'maj7']] },
    blues: { label: '12-bar blues', steps: [[0, 'dom7'], [0, 'dom7'], [0, 'dom7'], [0, 'dom7'], [5, 'dom7'], [5, 'dom7'], [0, 'dom7'], [0, 'dom7'], [7, 'dom7'], [5, 'dom7'], [0, 'dom7'], [7, 'dom7']] },
    fifties: { label: 'I – vi – IV – V', steps: [[0, 'maj'], [9, 'min'], [5, 'maj'], [7, 'maj']] },
    sad: { label: 'vi – IV – I – V', steps: [[9, 'min'], [5, 'maj'], [0, 'maj'], [7, 'maj']] }
  };

  const mod = (n, m) => ((n % m) + m) % m;
  const pcName = (pc, preferAlt) => (preferAlt && NAME_ALT[pc]) || NAMES[pc];
  const ROOT_INDEX = { C: 0, 'C#': 1, Db: 1, D: 2, 'D#': 3, Eb: 3, E: 4, F: 5, 'F#': 6, Gb: 6, G: 7, 'G#': 8, Ab: 8, A: 9, 'A#': 10, Bb: 10, B: 11 };

  /** Returns { frets:[6], fingers:[6], name, notes:[pc] } */
  function chord (rootPc, type) {
    const def = CHORDS[type] || CHORDS.maj;
    const key = NAMES[rootPc].replace('♯', '#').replace('♭', 'b') + ':' + type;
    const plain = ['C', 'D', 'E', 'F', 'G', 'A', 'B'][[0, 2, 4, 5, 7, 9, 11].indexOf(rootPc)];
    let voicing = plain && OPEN[plain + ':' + type];
    if (!voicing) {
      const cands = [];
      const rE = mod(rootPc - 4, 12) || 12;
      const rA = mod(rootPc - 9, 12) || 12;
      if (SHAPE_E[type]) cands.push({ r: rE, s: SHAPE_E[type] });
      if (SHAPE_A[type]) cands.push({ r: rA, s: SHAPE_A[type] });
      cands.sort((a, b) => a.r - b.r);
      const c = cands[0];
      voicing = [c.s[0].map((o) => (o === null ? null : o + c.r)), c.s[1].slice()];
    }
    const notes = def.iv.map((i) => mod(rootPc + i, 12));
    return { frets: voicing[0], fingers: voicing[1], name: pcName(rootPc) + def.sym, long: pcName(rootPc) + ' ' + CHORD_LONG[type], notes, key };
  }

  function degreeColor (iv) {
    if (iv === 0) return [C.root, 1];
    if (iv === 3 || iv === 4) return [C.third, 1];
    if (iv === 7 || iv === 6) return [C.fifth, iv === 7 ? 1 : 0.9];
    return [C.tone, 0.55];
  }

  function chordFrame (ch) {
    const f = blank();
    ch.frets.forEach((fret, s) => {
      if (fret === null || fret > 22) return;
      const finger = ch.fingers[s] || 0;
      f[idx(fret, s)] = [FINGER[fret === 0 ? 0 : finger || 1], 1];
    });
    return f;
  }

  function scaleFrame (rootPc, scaleKey, lo, hi) {
    const sc = SCALES[scaleKey] || SCALES.major;
    const f = blank();
    for (let s = 0; s < STRINGS; s++) {
      for (let fr = 0; fr < FRETS; fr++) {
        if (lo !== undefined && (fr < lo || fr > hi)) continue;
        const iv = mod(TUNING[s] + fr - rootPc, 12);
        if (sc.iv.includes(iv)) f[idx(fr, s)] = degreeColor(iv);
      }
    }
    return f;
  }

  const CAGED_ORDER = ['C', 'A', 'G', 'E', 'D'];
  const CAGED_WIN = { C: [-3, 0], A: [0, 3], G: [2, 5], E: [5, 8], D: [7, 10] };
  function cagedFrame (rootPc, shape) {
    const a = mod(rootPc - 9, 12);
    let [lo, hi] = CAGED_WIN[shape].map((x) => x + a);
    if (lo < 0) { lo += 12; hi += 12; }
    if (hi > 22) { lo -= 12; hi -= 12; }
    lo = Math.max(lo, 0);
    const f = blank();
    for (let s = 0; s < STRINGS; s++) {
      for (let fr = lo; fr <= hi; fr++) {
        const iv = mod(TUNING[s] + fr - rootPc, 12);
        if (iv === 0 || iv === 4 || iv === 7) f[idx(fr, s)] = degreeColor(iv);
      }
    }
    return { frame: f, lo, hi };
  }

  /* ------------------------------------------------------------- frames */
  const idx = (fret, s) => fret * STRINGS + s;
  const blank = () => new Array(N).fill(null);
  const hsl = (h, l = 60) => `hsl(${((h % 360) + 360) % 360} 100% ${l}%)`;

  // Light-show programs: (t seconds, opts) -> frame
  const PROGRAMS = {
    off: () => blank(),
    flow (t, o) {
      const f = blank();
      const speed = o.speed || 1;
      for (let fr = 0; fr < FRETS; fr++) {
        const col = o.color ? o.color : hsl(fr * 15 - t * 70 * speed, 62);
        const k = o.color ? 0.45 + 0.55 * (0.5 + 0.5 * Math.sin(fr * 0.55 - t * 3.2 * speed)) : 1;
        for (let s = 0; s < STRINGS; s++) f[idx(fr, s)] = [col, (o.level || 1) * k];
      }
      return f;
    },
    slide (t, o) {
      const f = blank();
      const pos = (Math.sin(t * 1.4 * (o.speed || 1)) * 0.5 + 0.5) * (FRETS - 1);
      for (let fr = 0; fr < FRETS; fr++) {
        const d = Math.abs(fr - pos);
        if (d > 3.2) continue;
        const k = Math.max(0, 1 - d / 3.2);
        for (let s = 0; s < STRINGS; s++) f[idx(fr, s)] = [o.color || C.cyan, k * (o.level || 1)];
      }
      return f;
    },
    sync (t, o) {
      const f = blank();
      const beat = (t * (o.bpm || 112)) / 60;
      const ph = beat % 1;
      const k = Math.pow(1 - ph, 2.2);
      const col = o.color || hsl(Math.floor(beat) * 47 + 190, 62);
      for (let i = 0; i < N; i++) f[i] = [col, 0.12 + 0.88 * k];
      return f;
    },
    hold (t, o) {
      const f = blank();
      for (let i = 0; i < N; i++) f[i] = [o.color || C.white, o.level || 0.9];
      return f;
    },
    // Ambient: slow cyan breath along the neck (hero idle)
    breathe (t, o) {
      const f = blank();
      for (let fr = 0; fr < FRETS; fr++) {
        const k = 0.5 + 0.5 * Math.sin(fr * 0.42 - t * 1.6);
        for (let s = 0; s < STRINGS; s++) f[idx(fr, s)] = [o.color || C.cyan, 0.25 + 0.75 * k * k];
      }
      return f;
    }
  };

  /* ------------------------------------------------------- <rv-guitar> */
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const loop = { set: new Set(), raf: 0, last: 0 };
  function tick (now) {
    loop.raf = 0;
    if (now - loop.last >= 33) {
      loop.last = now;
      loop.set.forEach((g) => g._tick(now / 1000));
    }
    if (loop.set.size) loop.raf = requestAnimationFrame(tick);
  }
  function wake () { if (!loop.raf && loop.set.size) loop.raf = requestAnimationFrame(tick); }

  class RvGuitar extends HTMLElement {
    connectedCallback () {
      if (this._init) return;
      this._init = true;
      this.leds = [];
      this.visible = false;
      this.program = null;
      this.opts = {};
      this.t0 = performance.now() / 1000;
      const host = this.querySelector('.rv-guitar__leds') || this.appendChild(Object.assign(document.createElement('span'), { className: 'rv-guitar__leds' }));
      host.setAttribute('aria-hidden', 'true');
      const frag = document.createDocumentFragment();
      for (let fr = 0; fr < FRETS; fr++) {
        for (let s = 0; s < STRINGS; s++) {
          const el = document.createElement('i');
          el.className = 'rv-led';
          frag.appendChild(el);
          this.leds.push(el);
        }
      }
      host.appendChild(frag);
      this.mq = window.matchMedia('(max-width: 749px)');
      this._onMQ = () => this.layout();
      this.mq.addEventListener('change', this._onMQ);
      this.layout();
      this.io = new IntersectionObserver((es) => {
        es.forEach((e) => {
          this.visible = e.isIntersecting;
          this.classList.toggle('is-visible', this.visible);
          if (this.visible) this.classList.add('is-entered');
          if (this.visible && !this.started) this.start();
          this._sync();
        });
      }, { rootMargin: '120px 0px' });
      this.io.observe(this);
      this.dispatchEvent(new CustomEvent('rv:guitar-ready', { bubbles: true }));
    }

    disconnectedCallback () {
      this.io && this.io.disconnect();
      this.mq && this.mq.removeEventListener('change', this._onMQ);
      loop.set.delete(this);
    }

    get orientation () {
      const o = this.dataset.orient || 'h';
      if (o === 'auto') return this.mq.matches ? 'v' : 'h';
      return o;
    }

    layout () {
      const o = this.orientation;
      this.dataset.layout = o;
      for (let fr = 0; fr < FRETS; fr++) {
        for (let s = 0; s < STRINGS; s++) {
          const [u, v] = MAP[fr][s];
          const el = this.leds[idx(fr, s)];
          const x = o === 'h' ? v : u;
          const y = o === 'h' ? 1 - u : v;
          el.style.left = (x * 100).toFixed(3) + '%';
          el.style.top = (y * 100).toFixed(3) + '%';
        }
      }
    }

    /** Paint a frame: array of [color, intensity] | null */
    paint (frame, { stagger = 0, order = 'fret' } = {}) {
      this._frame = frame;
      for (let i = 0; i < N; i++) {
        const el = this.leds[i];
        const v = frame[i];
        if (stagger && !reduceMotion()) {
          const fr = Math.floor(i / STRINGS);
          const s = i % STRINGS;
          el.style.transitionDelay = (order === 'string' ? s * stagger : fr * stagger) + 'ms';
        } else if (el.style.transitionDelay) {
          el.style.transitionDelay = '';
        }
        if (v) {
          el.style.setProperty('--c', v[0]);
          el.style.setProperty('--i', v[1].toFixed(3));
          if (!el.classList.contains('is-on')) el.classList.add('is-on');
        } else if (el.classList.contains('is-on')) {
          el.classList.remove('is-on');
        }
      }
    }

    clearDelays () { this.leds.forEach((el) => (el.style.transitionDelay = '')); }

    /** Run a light-show program by name */
    run (name, opts = {}) {
      if (name === 'hold') { this.show(PROGRAMS.hold(0, opts)); return; }
      this.program = PROGRAMS[name] || null;
      this.opts = opts;
      this.static = null;
      this.clearDelays();
      this.classList.toggle('is-animating', !!this.program);
      if (reduceMotion() && this.program) {
        // single representative frame
        this.paint(this.program(0.6, opts));
        this.program = null;
      }
      this._sync();
    }

    show (frame, opts) {
      this.program = null;
      this.static = frame;
      this.classList.remove('is-animating');
      this.paint(frame, opts);
      this._sync();
    }

    _sync () {
      if (this.program && this.visible && !document.hidden) { loop.set.add(this); wake(); } else loop.set.delete(this);
    }

    _tick (t) { if (this.program) this.paint(this.program(t - this.t0, this.opts)); }

    /** Power-on: sweep cyan from the nut to the body, then hand over. */
    powerOn (then) {
      if (reduceMotion()) { then && then(); return; }
      this.program = null;
      const sweep = blank();
      const f = blank();
      for (let i = 0; i < N; i++) { sweep[i] = [C.cyan, 1]; }
      this.paint(sweep, { stagger: 34 });
      clearTimeout(this._pt);
      this._pt = setTimeout(() => {
        this.paint(f, { stagger: 22 });
        this._pt = setTimeout(() => { this.clearDelays(); then && then(); }, 520);
      }, 23 * 34 + 260);
    }

    start () {
      this.started = true;
      const mode = this.dataset.mode || 'off';
      if (mode === 'off' || mode === 'manual') return;
      const color = this.dataset.ledColor || '';
      const go = () => {
        if (mode === 'chord') {
          const [r, t] = (this.dataset.chord || 'A:min7').split(':');
          this.show(chordFrame(chord(ROOT_INDEX[r] ?? 9, t || 'maj')), { stagger: 18, order: 'string' });
        } else if (mode === 'scale') {
          const [r, sc] = (this.dataset.scale || 'A:pentMin').split(':');
          this.show(scaleFrame(ROOT_INDEX[r] ?? 9, sc), { stagger: 16 });
        } else {
          this.run(mode, { color, level: +(this.dataset.level || 1), speed: +(this.dataset.speed || 1) });
        }
      };
      if (this.hasAttribute('data-power')) {
        this.powerOn(go);
      } else go();
    }
  }
  if (!customElements.get('rv-guitar')) customElements.define('rv-guitar', RvGuitar);
  document.addEventListener('visibilitychange', () => loop.set.forEach((g) => g._sync()));

  /* --------------------------------------------------------------- synth */
  const Synth = {
    ctx: null,
    cache: new Map(),
    on: false,
    ensure () {
      if (!this.ctx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        this.ctx = new AC();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0.32;
        const comp = this.ctx.createDynamicsCompressor();
        this.master.connect(comp).connect(this.ctx.destination);
      }
      if (this.ctx.state === 'suspended') this.ctx.resume();
      return this.ctx;
    },
    buffer (midi) {
      if (this.cache.has(midi)) return this.cache.get(midi);
      const ctx = this.ctx;
      const sr = ctx.sampleRate;
      const freq = 440 * Math.pow(2, (midi - 69) / 12);
      const len = Math.floor(sr * 2.2);
      const buf = ctx.createBuffer(1, len, sr);
      const d = buf.getChannelData(0);
      const p = Math.max(2, Math.round(sr / freq));
      const ring = new Float32Array(p);
      for (let i = 0; i < p; i++) ring[i] = (Math.random() * 2 - 1) * 0.9;
      let k = 0;
      const damp = 0.996 - (midi - 40) * 0.0004;
      for (let i = 0; i < len; i++) {
        const a = ring[k];
        const b = ring[(k + 1) % p];
        const v = (a + b) * 0.5 * damp;
        ring[k] = v;
        d[i] = a;
        k = (k + 1) % p;
      }
      this.cache.set(midi, buf);
      return buf;
    },
    strum (midis, gap = 0.022) {
      if (!this.on) return;
      const ctx = this.ensure();
      if (!ctx) return;
      const now = ctx.currentTime + 0.02;
      midis.forEach((m, i) => {
        const src = ctx.createBufferSource();
        src.buffer = this.buffer(m);
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.0001, now + i * gap);
        g.gain.exponentialRampToValueAtTime(0.9, now + i * gap + 0.006);
        g.gain.exponentialRampToValueAtTime(0.0001, now + i * gap + 2.1);
        const lp = ctx.createBiquadFilter();
        lp.type = 'lowpass';
        lp.frequency.value = 3800;
        src.connect(lp).connect(g).connect(this.master);
        src.start(now + i * gap);
        src.stop(now + i * gap + 2.2);
      });
    },
    tick (accent) {
      if (!this.on) return;
      const ctx = this.ensure();
      if (!ctx) return;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = accent ? 1760 : 1320;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(accent ? 0.25 : 0.12, ctx.currentTime + 0.002);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.05);
      o.connect(g).connect(this.master);
      o.start();
      o.stop(ctx.currentTime + 0.06);
    }
  };
  const chordMidis = (ch) => ch.frets.map((f, s) => (f === null ? null : TUNING[s] + f)).filter((m) => m !== null);


  /* ===================================================== section: demo */
  const $ = (sel, r) => r.querySelector(sel);
  const $$ = (sel, r) => Array.from(r.querySelectorAll(sel));
  const FX_TEXT = {
    flow: 'Light streams along the neck',
    slide: 'A band of light glides fret to fret',
    sync: 'The whole board pulses on the beat',
    hold: 'One steady colour, every fret'
  };
  const SCALE_SHORT = { pentMin: 'minor pentatonic', pentMaj: 'major pentatonic', major: 'major', minor: 'natural minor', blues: 'blues', dorian: 'dorian', mixolydian: 'mixolydian', harmMin: 'harmonic minor' };

  function radioGroup (group, onPick) {
    const items = $$('[role="radio"]', group);
    const set = (el, focus) => {
      items.forEach((b) => { const on = b === el; b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1; });
      if (focus) el.focus();
      onPick(el);
    };
    items.forEach((b) => {
      b.tabIndex = b.getAttribute('aria-checked') === 'true' ? 0 : -1;
      b.addEventListener('click', () => set(b));
      b.addEventListener('keydown', (e) => {
        const i = items.indexOf(b);
        let n = -1;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = (i + 1) % items.length;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = (i - 1 + items.length) % items.length;
        if (n > -1) { e.preventDefault(); set(items[n], true); }
      });
    });
    if (!items.some((b) => b.tabIndex === 0) && items[0]) items[0].tabIndex = 0;
    return { set: (pred) => { const el = items.find(pred); el && set(el); } };
  }

  function initDemo (sec) {
    if (sec._rvfb) return;
    sec._rvfb = true;
    const g = $('rv-guitar', sec);
    const crop = $('.rv-fb__crop', sec);
    const scroller = $('[data-rv-fb-scroller]', sec);
    const nameEl = $('[data-rv-fb-name]', sec);
    const notesEl = $('[data-rv-fb-notes]', sec);
    const modeEl = $('[data-rv-fb-mode]', sec);
    const liveEl = $('[data-rv-fb-live]', sec);
    const statEl = $('[data-rv-stat]', sec);
    const seqEl = $('[data-rv-seq]', sec);
    const beatsEl = $('[data-rv-beats]', sec);
    const playBtn = $('[data-rv-play]', sec);
    const bpmIn = $('[data-rv-bpm]', sec);
    const bpmOut = $('[data-rv-bpm-out]', sec);
    const rootLabel = $('[data-rv-root-label]', sec);
    const st = { tab: 'chords', root: 9, type: 'min7', scale: 'pentMin', shape: 'E', prog: 'pop', bpm: 92, fx: 'flow', color: '', playing: false, step: 0, beat: 0 };
    let timer = 0;
    const cs = getComputedStyle(crop);
    const X0 = parseFloat(cs.getPropertyValue('--x0')) || 0;
    const X1 = parseFloat(cs.getPropertyValue('--x1')) || 1;

    // fret numbers
    const fretsEl = $('[data-rv-fb-frets]', sec);
    if (fretsEl && !fretsEl.children.length) {
      [0, 3, 5, 7, 9, 12, 15, 17, 19, 21].forEach((f) => {
        const li = document.createElement('li');
        const v = MAP[f][0][1];
        li.style.left = (((v - X0) / (X1 - X0)) * 100).toFixed(3) + '%';
        li.textContent = f === 0 ? 'Open' : f;
        if (f === 12) li.className = 'is-octave';
        fretsEl.appendChild(li);
      });
    }

    const noteList = (pcs) => pcs.map((p) => pcName(p)).join(' · ');
    const setName = (main, sub) => {
      nameEl.textContent = '';
      nameEl.append(document.createTextNode(main));
      if (sub) { const sm = document.createElement('small'); sm.textContent = sub; nameEl.append(sm); }
    };

    function centerOn (frame) {
      if (!scroller || scroller.scrollWidth <= scroller.clientWidth + 4) return;
      let sum = 0; let n = 0;
      frame.forEach((v, i) => { if (v) { sum += MAP[Math.floor(i / STRINGS)][0][1]; n++; } });
      if (!n) return;
      const v = sum / n;
      const x = ((v - X0) / (X1 - X0)) * crop.offsetWidth;
      scroller.scrollTo({ left: Math.max(0, x - scroller.clientWidth / 2), behavior: reduceMotion() ? 'auto' : 'smooth' });
    }

    function stopProg () {
      st.playing = false;
      clearTimeout(timer);
      playBtn && playBtn.setAttribute('aria-pressed', 'false');
      sec.classList.remove('is-playing');
      beatsEl && $$('i', beatsEl).forEach((i) => i.classList.remove('is-on'));
    }

    function progChord (k) {
      const p = PROGRESSIONS[st.prog];
      const [deg, type] = p.steps[k % p.steps.length];
      return chord(mod(st.root + deg, 12), type);
    }

    function renderSeq () {
      if (!seqEl) return;
      const p = PROGRESSIONS[st.prog];
      seqEl.innerHTML = '';
      p.steps.forEach((_s, k) => {
        const li = document.createElement('li');
        li.textContent = progChord(k).name;
        if (k === st.step) li.className = 'is-current';
        seqEl.appendChild(li);
      });
    }

    function showProgStep (strum) {
      const ch = progChord(st.step);
      g.show(chordFrame(ch), { stagger: 14, order: 'string' });
      setName(ch.name);
      notesEl.textContent = PROGRESSIONS[st.prog].label + ' · ' + 'Key of ' + pcName(st.root);
      renderSeq();
      centerOn(chordFrame(ch));
      if (strum) Synth.strum(chordMidis(ch));
      liveEl.textContent = ch.long;
    }

    function beatLoop () {
      const dots = beatsEl ? $$('i', beatsEl) : [];
      dots.forEach((d2, i) => d2.classList.toggle('is-on', i === st.beat));
      if (st.beat === 0) showProgStep(true); else Synth.tick(false);
      st.beat = (st.beat + 1) % 4;
      if (st.beat === 0) st.step = (st.step + 1) % PROGRESSIONS[st.prog].steps.length;
      timer = setTimeout(beatLoop, 60000 / st.bpm);
    }

    function render (opts = {}) {
      const strum = opts.strum;
      sec.dataset.tab = st.tab;
      $$('[data-show]', sec).forEach((el) => { el.hidden = !el.dataset.show.split(' ').includes(st.tab); });
      $$('[data-legend]', sec).forEach((el) => {
        const want = st.tab === 'chords' || st.tab === 'prog' ? 'fingers' : (st.tab === 'scales' || st.tab === 'caged') ? 'degrees' : 'none';
        el.hidden = el.dataset.legend !== want;
      });
      statEl.textContent = statEl.dataset[st.tab] || '';
      const activeTab = $('[role="tab"][aria-selected="true"]', sec);
      modeEl.textContent = activeTab ? activeTab.textContent : '';
      if (rootLabel) rootLabel.textContent = st.tab === 'prog' ? rootLabel.dataset.key : rootLabel.dataset.root;
      if (st.tab !== 'prog') stopProg();

      if (st.tab === 'chords') {
        const ch = chord(st.root, st.type);
        const fr = chordFrame(ch);
        g.show(fr, { stagger: 16, order: 'string' });
        setName(ch.name);
        notesEl.textContent = noteList(ch.notes);
        liveEl.textContent = ch.long + ': ' + noteList(ch.notes);
        centerOn(fr);
        if (strum) Synth.strum(chordMidis(ch));
      } else if (st.tab === 'scales') {
        const fr = scaleFrame(st.root, st.scale);
        g.show(fr, { stagger: 10 });
        setName(pcName(st.root), SCALE_SHORT[st.scale]);
        const sc = SCALES[st.scale];
        notesEl.textContent = noteList(sc.iv.map((i) => mod(st.root + i, 12)));
        liveEl.textContent = pcName(st.root) + ' ' + SCALE_SHORT[st.scale];
        if (scroller) scroller.scrollTo({ left: 0, behavior: 'smooth' });
        if (strum) Synth.strum(sc.iv.concat([12]).map((i) => 52 + mod(st.root - 4, 12) + i), 0.11);
      } else if (st.tab === 'caged') {
        let fr; let lo = 0; let hi = 22;
        if (st.shape === 'all') {
          fr = blank();
          CAGED_ORDER.forEach((sh) => { const r = cagedFrame(st.root, sh); r.frame.forEach((v, i) => { if (v) fr[i] = v; }); });
          setName(pcName(st.root), 'all five shapes');
        } else {
          const r = cagedFrame(st.root, st.shape);
          fr = r.frame; lo = r.lo; hi = r.hi;
          setName(pcName(st.root), st.shape + ' shape');
        }
        g.show(fr, { stagger: 12 });
        const third = pcName(mod(st.root + 4, 12));
        const fifth = pcName(mod(st.root + 7, 12));
        notesEl.textContent = 'Root ' + pcName(st.root) + ' · 3rd ' + third + ' · 5th ' + fifth + (st.shape === 'all' ? '' : ' · Frets ' + lo + '–' + hi);
        liveEl.textContent = nameEl.textContent;
        centerOn(fr);
        if (strum) Synth.strum([0, 4, 7, 12].map((i) => 52 + mod(st.root - 4, 12) + i), 0.09);
      } else if (st.tab === 'prog') {
        st.step = 0; st.beat = 0;
        showProgStep(strum && !st.playing);
      } else if (st.tab === 'light') {
        g.run(st.fx, { color: st.color, bpm: 112 });
        setName(st.fx.charAt(0).toUpperCase() + st.fx.slice(1));
        notesEl.textContent = FX_TEXT[st.fx];
        liveEl.textContent = nameEl.textContent + ': ' + FX_TEXT[st.fx];
        if (scroller) scroller.scrollTo({ left: 0, behavior: 'smooth' });
      }
    }

    // tabs
    const tabs = $$('[role="tab"]', sec);
    const ink = $('.rv-fb__tab-ink', sec);
    const moveInk = () => {
      const t = tabs.find((x) => x.getAttribute('aria-selected') === 'true');
      if (!t || !ink) return;
      ink.style.width = t.offsetWidth + 'px';
      ink.style.transform = 'translateX(' + t.offsetLeft + 'px)';
    };
    const pickTab = (t, focus) => {
      tabs.forEach((x) => { const on = x === t; x.setAttribute('aria-selected', on ? 'true' : 'false'); x.tabIndex = on ? 0 : -1; });
      if (focus) t.focus();
      st.tab = t.dataset.tab;
      moveInk();
      render({ strum: true });
    };
    tabs.forEach((t) => {
      t.addEventListener('click', () => pickTab(t));
      t.addEventListener('keydown', (e) => {
        const i = tabs.indexOf(t);
        if (e.key === 'ArrowRight') { e.preventDefault(); pickTab(tabs[(i + 1) % tabs.length], true); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); pickTab(tabs[(i - 1 + tabs.length) % tabs.length], true); }
      });
    });
    window.addEventListener('resize', moveInk, { passive: true });
    document.fonts && document.fonts.ready.then(moveInk);

    // radiogroups
    $$('[role="radiogroup"]', sec).forEach((grp) => radioGroup(grp, (el) => {
      const ds = el.dataset;
      if (ds.root !== undefined) st.root = +ds.root;
      if (ds.type) st.type = ds.type;
      if (ds.scale) st.scale = ds.scale;
      if (ds.shape) st.shape = ds.shape;
      if (ds.prog) { st.prog = ds.prog; st.step = 0; st.beat = 0; }
      if (ds.fx) st.fx = ds.fx;
      if (ds.color !== undefined && el.classList.contains('rv-fb__swatch')) st.color = ds.color;
      if (st.tab === 'prog' && st.playing) { renderSeq(); return; }
      render({ strum: true });
    }));

    // sound
    const soundBtn = $('[data-rv-sound]', sec);
    if (soundBtn) {
      const lab = $('.rv-fb__sound-label', soundBtn);
      soundBtn.addEventListener('click', () => {
        Synth.on = !Synth.on;
        if (Synth.on) Synth.ensure();
        soundBtn.setAttribute('aria-pressed', Synth.on ? 'true' : 'false');
        lab.textContent = Synth.on ? lab.dataset.on : lab.dataset.off;
        if (Synth.on) render({ strum: true });
      });
    }

    // transport
    if (playBtn) playBtn.addEventListener('click', () => {
      if (st.playing) { stopProg(); return; }
      st.playing = true;
      playBtn.setAttribute('aria-pressed', 'true');
      sec.classList.add('is-playing');
      if (Synth.on) Synth.ensure();
      st.beat = 0;
      beatLoop();
    });
    if (bpmIn) bpmIn.addEventListener('input', () => { st.bpm = +bpmIn.value; bpmOut.textContent = st.bpm; });

    // pause the transport off-screen
    new IntersectionObserver((es) => es.forEach((e) => { if (!e.isIntersecting && st.playing) stopProg(); })).observe(sec);

    const go = () => { moveInk(); render(); };
    if (g && g.leds) go(); else sec.addEventListener('rv:guitar-ready', go, { once: true });
  }

  function init404 (g) {
    if (g._rv404) return;
    g._rv404 = true;
    const seq = [4, 0, 4];
    let k = 0;
    const step = () => {
      const f = blank();
      for (let s = 0; s < STRINGS; s++) f[idx(seq[k], s)] = [k === 1 ? C.white : C.cyan, 1];
      g.show(f, { stagger: 40, order: 'string' });
      k = (k + 1) % seq.length;
    };
    const go = () => { step(); if (!reduceMotion()) setInterval(step, 900); };
    if (g.leds) go(); else g.addEventListener('rv:guitar-ready', go, { once: true });
  }

  /* ============================================== section: light show */
  function initLight (sec) {
    if (sec._rvlight) return;
    const g = $('rv-guitar', sec);
    const frame = $('[data-rv-light-frame]', sec);
    const fxGroup = $('.rv-light__fx', sec);
    const items = fxGroup ? $$('[role="radio"]', fxGroup) : [];
    if (!g || !frame || !items.length) return;
    sec._rvlight = true;
    const chipGroup = $('.rv-light__colours', sec);
    const nowEl = $('[data-rv-light-now]', sec);
    const colEl = $('[data-rv-light-colour]', sec);
    const autoName = colEl ? colEl.textContent : '';
    const interval = Math.max(3, parseFloat(sec.dataset.interval) || 5) * 1000;
    sec.style.setProperty('--rv-interval', interval + 'ms');
    const GLOW = { flow: C.cyan, slide: C.cyan, sync: '#b46bff', hold: C.white };
    const st = { i: 0, colour: '', auto: !reduceMotion(), inView: false, powered: false, timer: 0 };
    if (st.auto) sec.classList.add('is-auto');

    const restartBar = () => {
      const bar = $('.rv-light__bar', items[st.i]);
      if (!bar) return;
      bar.style.animation = 'none'; void bar.offsetWidth; bar.style.animation = '';
    };
    const apply = () => {
      const it = items[st.i];
      const fx = it.dataset.fx || 'flow';
      items.forEach((b, k) => { b.setAttribute('aria-checked', k === st.i ? 'true' : 'false'); b.tabIndex = k === st.i ? 0 : -1; });
      g.run(fx, { color: st.colour, bpm: 112, level: fx === 'hold' ? 0.9 : 1 });
      frame.dataset.fx = fx;
      frame.toggleAttribute('data-auto-colour', !st.colour);
      frame.style.setProperty('--glow', st.colour || GLOW[fx] || C.cyan);
      if (nowEl) nowEl.textContent = it.dataset.name || fx;
      if (st.auto) restartBar();
    };
    const schedule = () => {
      clearTimeout(st.timer);
      if (st.auto && st.inView && !document.hidden) st.timer = setTimeout(() => { st.i = (st.i + 1) % items.length; apply(); schedule(); }, interval);
    };
    const stopAuto = () => { st.auto = false; clearTimeout(st.timer); sec.classList.remove('is-auto'); };

    radioGroup(fxGroup, (el) => { st.i = items.indexOf(el); stopAuto(); apply(); });
    if (chipGroup) radioGroup(chipGroup, (el) => {
      st.colour = el.dataset.colour || '';
      if (colEl) colEl.textContent = st.colour ? el.dataset.name : autoName;
      stopAuto();
      apply();
    });

    const go = () => {
      new IntersectionObserver((es) => es.forEach((e) => {
        st.inView = e.isIntersecting;
        if (st.inView && !st.powered) {
          st.powered = true;
          g.powerOn(() => { apply(); schedule(); });
          return;
        }
        if (st.inView && st.auto) { restartBar(); schedule(); } else clearTimeout(st.timer);
      }), { threshold: 0.3 }).observe(frame);
      document.addEventListener('visibilitychange', schedule);
    };
    if (g.leds) go(); else g.addEventListener('rv:guitar-ready', go, { once: true });
  }

  function initAll (root) {
    (root || document).querySelectorAll('[data-rv-404]').forEach(init404);
    (root || document).querySelectorAll('[data-rv-fb]').forEach(initDemo);
    (root || document).querySelectorAll('[data-rv-light]').forEach(initLight);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll()); else initAll();
  document.addEventListener('shopify:section:load', (e) => initAll(e.target));

  /* -------------------------------------------------- public namespace */
  window.RV = Object.assign(window.RV || {}, {
    MAP, FRETS, STRINGS, NAMES, CHORDS, SCALES, PROGRESSIONS, CAGED_ORDER, COLORS: C, ROOT_INDEX,
    chord, chordFrame, scaleFrame, cagedFrame, programs: PROGRAMS, Synth, chordMidis, pcName, blank
  });
  document.dispatchEvent(new CustomEvent('rv:engine-ready'));
})();
